import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../../utils/functions";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";

// Import BOQ components
import BOQSummaryCard from "./BOQSummaryCard";
import ConstructionTypesChart from "./ConstructionTypesChart";
import FeatureCategoriesAnalysis from "./FeatureCategoriesAnalysis";
import FeatureBudgetAnalysis from "./FeatureBudgetAnalysis";
import VillageCoverageAnalysis from "./VillageCoverageAnalysis";
import NoData from "../../common/NoData";

// Import Implementation Dashboard
import ImplementationDashboard from "../BoqImplementation";
import MonitoringDashboard from "../BoqMonitoring/Dashboard";

const BoqsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  /** Permissions */
  const canViewBOQ = permissions.includes("dashboard-boq");
  const canViewImplementation = permissions.includes("dashboard-boq-implementation");
  const canViewMonitoring = permissions.includes("dashboard-boq-monitoring");

  /** Main Tabs - BOQ, Implementation, Monitoring */
  const [activeMainTab, setActiveMainTab] = useState(
    canViewBOQ ? "boq" : canViewImplementation ? "implementation" : canViewMonitoring ? "monitoring" : "boq"
  );

  const [dashboardData, setDashboardData] = useState({
    counts: null,
    construction: null,
    coverage: null,
    costByDistrict: null,
    costByCategory: null,
    budgetByFeature: null,
  });

  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });

  const [loading, setLoading] = useState({
    counts: false,
    construction: false,
    coverage: false,
    costByDistrict: false,
    costByCategory: false,
    budgetByFeature: false,
  });

  // Add this ref to track the current filters
  const filtersRef = useRef(filters);

  // Update the ref whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Load initial data when BOQ tab is active
  useEffect(() => {
    if (activeMainTab === "boq") {
      fetchAllDashboardData();
    }
  }, [activeMainTab]);

  // Fetch all dashboard data for BOQ
  const fetchAllDashboardData = async (newFilters = null) => {
    if (!canViewBOQ) {
      console.warn("User doesn't have permission to view BOQ dashboard");
      return;
    }

    // Use newFilters if provided, otherwise use current filters from ref
    const currentFilters = newFilters || filtersRef.current;

    setLoading({
      counts: true,
      construction: true,
      coverage: true,
      costByDistrict: true,
      costByCategory: true,
      budgetByFeature: true,
    });

    try {
      // Define all API endpoints
      const endpoints = [
        { key: "counts", url: "CountDashboardBoq" },
        { key: "construction", url: "construction-types" },
        { key: "coverage", url: "village-coverage" },
        { key: "costByDistrict", url: "cost-by-district" },
        { key: "costByCategory", url: "cost-by-category" },
        { key: "budgetByFeature", url: "feature-cost-across-feature-types" },
      ];

      // Create promises for each endpoint
      const promises = endpoints.map(async ({ key, url }) => {
        try {
          const response = await fetchData(
            url,
            "GET",
            null,
            {},
            currentFilters, // Use currentFilters directly
            navigate,
            "/boq-dashboard",
            dispatch
          );

          if (response.success) {
            setDashboardData((prev) => ({
              ...prev,
              [key]: response.data,
            }));
          } else {
            console.error(`Failed to fetch ${key}:`, response.message);
            setDashboardData((prev) => ({
              ...prev,
              [key]: {},
            }));
          }
        } catch (error) {
          console.error(`Error fetching ${key}:`, error);
          setDashboardData((prev) => ({
            ...prev,
            [key]: {},
          }));
        } finally {
          setLoading((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      });

      await Promise.all(promises);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading({
        counts: false,
        construction: false,
        coverage: false,
        costByDistrict: false,
        costByCategory: false,
        budgetByFeature: false,
      });
    }
  };

  const handleMainTabChange = (tab) => {
    setActiveMainTab(tab);

    // Create reset filters
    const resetFilterValues = {
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    };

    // Reset filters
    setFilters(resetFilterValues);
    filtersRef.current = resetFilterValues;

    if (tab !== "boq") {
      // Reset data for other tabs
      setDashboardData({
        counts: null,
        construction: null,
        coverage: null,
        costByDistrict: null,
        costByCategory: null,
        budgetByFeature: null,
      });
    } else {
      // Fetch data for BOQ tab with reset filters
      setTimeout(() => {
        fetchAllDashboardData(resetFilterValues);
      }, 100);
    }
  };

  const handleFilterChange = (appliedFilters) => {

    // Update state
    setFilters(appliedFilters);

    // Update ref immediately
    filtersRef.current = appliedFilters;

    // Fetch data with the new filters - pass appliedFilters directly
    if (activeMainTab === "boq") {
      fetchAllDashboardData(appliedFilters);
    }
  };

  const resetFilters = () => {
    const resetFilterValues = {
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    };

    // Update state
    setFilters(resetFilterValues);

    // Update ref immediately
    filtersRef.current = resetFilterValues;

    if (activeMainTab === "boq") {
      // Fetch with reset filters
      setTimeout(() => {
        fetchAllDashboardData(resetFilterValues);
      }, 100);
    }
  };

  // Check if any data is loading
  const isLoading = Object.values(loading).some((status) => status);

  // Format function for large numbers (M for millions, K for thousands)
  const formatLargeNumber = (num) => {
    if (!num && num !== 0) return "0";
    if (typeof num === "string") {
      // If it's already formatted, return as is
      if (num.includes("M") || num.includes("K")) return num;
      // Try to parse as number
      const parsed = parseFloat(num.replace(/,/g, ""));
      if (isNaN(parsed)) return num;
      num = parsed;
    }

    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }
    return num.toString();
  };

  // Calculate overall statistics for the dashboard
  const getOverallStats = () => {
    const constructionSummary = dashboardData.construction?.summary || {};
    const coverageSummary = dashboardData.coverage?.summary || {};
    const counts = dashboardData.counts?.data || {};
    const budgetByFeatureSummary =
      dashboardData.budgetByFeature?.budgetDistribution?.summary || {};

    // Get raw values
    const totalBoqs =
      constructionSummary.totalBoqs || counts.totalBoqProposed || 0;
    const totalCost =
      budgetByFeatureSummary.totalBudget || constructionSummary.totalCost || 0;
    const avgCostPerBoq =
      constructionSummary.totalBoqs > 0
        ? Math.round(
            constructionSummary.totalCost / constructionSummary.totalBoqs
          )
        : 0;

    // Format the values
    const totalBoqsFormatted =
      totalBoqs >= 1000000
        ? `${(totalBoqs / 1000000).toFixed(1)}M`
        : totalBoqs >= 1000
        ? `${(totalBoqs / 1000).toFixed(1)}K`
        : totalBoqs.toString();

    const totalCostFormatted = formatLargeNumber(totalCost);
    const avgCostPerBoqFormatted = formatLargeNumber(avgCostPerBoq);

    return {
      totalBoqs,
      totalBoqsFormatted,

      totalCost,
      totalCostFormatted,

      totalVillages: coverageSummary.totalVillages || 0,
      villagesWithBoqs: coverageSummary.totalVillagesWithBoqs || 0,
      coveragePercentage: coverageSummary.overallCoveragePercentage || 0,
      totalDistricts: coverageSummary.totalDistricts || 0,

      avgCostPerBoq,
      avgCostPerBoqFormatted,
    };
  };

  const stats = getOverallStats();
  const has = {
    summary:
      stats.totalBoqs > 0 ||
      (dashboardData.construction?.boqsByConstructionType || []).length > 0,
    features:
      (dashboardData.costByCategory?.costByFeatureCategory || []).length > 0 ||
      (dashboardData.budgetByFeature?.budgetDistribution?.byFeature || []).length > 0,
    coverage: (dashboardData.coverage?.districtWiseCoverage || []).length > 0,
  };

  return (
    <div className="w-full px-4 bg-gray-50">
      {/* ----- MAIN TABS ----- */}
      <div className="flex flex-wrap border-b border-gray-200 mb-4 bg-white rounded-lg shadow-sm">
        {canViewBOQ && (
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              activeMainTab === "boq"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:text-[#137e81]"
            }`}
            onClick={() => handleMainTabChange("boq")}
          >
            BOQ Dashboard
          </button>
        )}
        {canViewImplementation && (
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              activeMainTab === "implementation"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:text-[#137e81]"
            }`}
            onClick={() => handleMainTabChange("implementation")}
          >
            Implementation Dashboard
          </button>
        )}
        {canViewMonitoring && (
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              activeMainTab === "monitoring"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:text-[#137e81]"
            }`}
            onClick={() => handleMainTabChange("monitoring")}
          >
            Monitoring Dashboard
          </button>
        )}
      </div>

      {/* Render appropriate dashboard based on active tab */}
      {activeMainTab === "boq" && (
        <>
          {/* ----- FILTER SECTION ----- */}
          <div className="w-full flex flex-col justify-between mb-4">
            <DashboardSearchFilters
              accordionTitle="BOQ Filters"
              defaultExpanded={false}
              fetchOnChange={false}
              apiRoutes={{
                districts: "districts",
                tehsils: "tehsils",
                ucs: "ucs",
                dehs: "dehs",
                villages: "villages",
              }}
              dateFields={["created_at", "updated_at"]}
              onApply={handleFilterChange}
            />
          </div>

          {/* ----- PERMISSION CHECK ----- */}
          {!canViewBOQ && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <svg
                className="w-12 h-12 text-yellow-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-yellow-700 mb-2 text-balance">
                Access Denied
              </h3>
              <p className="text-yellow-600">
                You don't have permission to view the BOQ Dashboard. Please
                contact your administrator.
              </p>
            </div>
          )}

          {/* ----- LOADING ----- */}
          {isLoading && canViewBOQ && (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-b-2 border-teal-700 rounded-full mx-auto"></div>
              <p className="mt-2">Loading BOQ Dashboard Data...</p>
            </div>
          )}

          {/* ----- MAIN DASHBOARD CONTENT ----- */}
          {!isLoading && canViewBOQ && (
            <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5">
              <div className="space-y-6">
                {/* First Row - BOQ Summary and Construction Types in Single Row */}
                {has.summary && (
                <div className="bg-white rounded-lg  shadow-sm mt-3 my-3">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column: BOQ Summary */}
                    <div className="flex flex-col justify-center">
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 text-balance">
                          BOQ Summary ({stats.totalBoqsFormatted})
                        </h3>
                        <BOQSummaryCard stats={stats} />
                      </div>
                    </div>

                    {/* Right Column: Construction Types */}
                    <div className="flex flex-col justify-center">
                      <div className="text-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-800 text-balance">
                          Construction Types
                        </h3>
                      </div>
                      {dashboardData.construction ? (
                        <ConstructionTypesChart
                          data={
                            dashboardData.construction.boqsByConstructionType
                          }
                          centerValue={stats.totalBoqsFormatted}
                          centerLabel="Total"
                        />
                      ) : (
                        <NoData />
                      )}

                      {/* Compact Stats Below Chart */}
                      {/* {dashboardData.construction && (
                        <div className="grid grid-cols-3 gap-2 mt-4">
                          <div className="text-center bg-blue-50 rounded p-2">
                            <div className="text-xs text-blue-700">
                              Total Types
                            </div>
                            <div className="text-sm font-bold text-blue-800">
                              {dashboardData.construction.boqsByConstructionType
                                ?.length || 0}
                            </div>
                          </div>
                          <div className="text-center bg-green-50 rounded p-2">
                            <div className="text-xs text-green-700">
                              Total BOQs
                            </div>
                            <div className="text-sm font-bold text-green-800">
                              {stats.totalBoqs}
                            </div>
                          </div>
                          <div className="text-center bg-purple-50 rounded p-2">
                            <div className="text-xs text-purple-700">
                              Avg Cost
                            </div>
                            <div className="text-sm font-bold text-purple-800">
                              {stats.avgCostPerBoqFormatted}
                            </div>
                          </div>
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
                )}

                {/* Second Row - Feature Categories and Feature Budget Analysis */}
                {has.features && (<>
                <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Feature Categories Analysis - Takes 6 columns */}
                  <div className="lg:col-span-6">
                    <h3 className="text-xl  text-[#3A3A3A] font-bold uppercase text-center text-balance">
                      Feature Categories Analysis
                    </h3>
                    <FeatureCategoriesAnalysis
                      data={dashboardData.costByCategory}
                      loading={loading.costByCategory}
                    />
                  </div>

                  {/* Feature Budget Analysis - Takes 6 columns */}
                  <div className="lg:col-span-6">
                    <h3 className="text-xl my-0 p-0 text-[#3A3A3A] font-bold uppercase text-center text-balance">
                      Feature Budget Analysis
                    </h3>
                    <FeatureBudgetAnalysis
                      data={dashboardData.budgetByFeature}
                      loading={loading.budgetByFeature}
                    />
                  </div>
                </div>
                </>)}

                {/* Third Row - Village Coverage */}
                {has.coverage && (<>
                <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Village Coverage - Takes 12 columns */}
                  <div className="lg:col-span-12">
                    <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance">
                      District-wise Village Coverage Analysis
                    </h3>
                    <VillageCoverageAnalysis
                      data={dashboardData.coverage?.districtWiseCoverage || []}
                      summary={dashboardData.coverage?.summary || {}}
                      loading={loading.coverage}
                    />
                  </div>
                </div>
                </>)}

                {/* Nothing to show for the current filters */}
                {!has.summary && !has.features && !has.coverage && (
                    <div className="text-center py-10">
                      <svg
                        className="w-16 h-16 text-gray-400 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-gray-500">
                        No dashboard data available
                      </p>
                      <button
                        onClick={() =>
                          fetchAllDashboardData(filtersRef.current)
                        }
                        className="mt-4 px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f6b6d]"
                      >
                        Retry Loading Data
                      </button>
                    </div>
                  )}
              </div>
            </div>
          )}
        </>
      )}
      {activeMainTab === "implementation" && <ImplementationDashboard />}
      {activeMainTab === "monitoring" && <MonitoringDashboard />}
      {/* 

      {activeMainTab === "monitoring" && (
        <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 py-6">
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 text-white bg-[#137e81] rounded-full">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 text-balance">
                Monitoring Dashboard
              </h3>
              <p className="text-gray-600 mb-6">
                Monitor ongoing projects and performance metrics
              </p>
              <div className="max-w-2xl mx-auto">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4 text-balance">
                    Coming Soon
                  </h4>
                  <p className="text-gray-600">
                    Monitoring dashboard components are under development and
                    will be available soon.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default BoqsDashboard;
